import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { scrollToId } from "@/lib/scroll";
import { LeafMark } from "@/components/LeafMark";
import { EASE } from "@/components/motion";

const LINKS = [
  { label: "Collection", href: "#collection" },
  { label: "Our Story", href: "#story" },
  { label: "Brew Guide", href: "#brew-guide" },
  { label: "Contact", href: "#contact" },
];

const Nav = () => {
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (href) => {
    setMenuOpen(false);
    scrollToId(href);
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-stone-950/60 backdrop-blur-md"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <button
            data-testid="nav-brand"
            onClick={() => go("#home")}
            className="group flex items-center gap-3"
          >
            <LeafMark className="h-7 w-7 text-amber-600 transition-colors duration-300 group-hover:text-amber-500" />
            <span className="text-left leading-none">
              <span className="block font-serif text-xl tracking-tight text-stone-100">
                Amber Brew
              </span>
              <span className="block text-[9px] uppercase tracking-[0.4em] text-stone-500">
                Tea Company
              </span>
            </span>
          </button>

          <nav className="hidden items-center gap-9 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => go(l.href)}
                className="group relative text-[13px] font-light tracking-[0.12em] text-stone-400 transition-colors duration-300 hover:text-amber-500"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-amber-600 transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="nav-cart-button"
              onClick={() => setOpen(true)}
              className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-stone-300 transition-colors duration-300 hover:border-amber-600/50 hover:text-amber-500"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-[18px] w-[18px]" />
              {count > 0 && (
                <span
                  data-testid="nav-cart-count"
                  className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-600 px-1 text-[10px] font-semibold text-stone-950"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="nav-menu-button"
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-stone-300 transition-colors duration-300 hover:border-amber-600/50 hover:text-amber-500 md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] flex flex-col bg-stone-950/95 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="flex h-16 items-center justify-between px-5">
              <LeafMark className="h-7 w-7 text-amber-600" />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-stone-300"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                  onClick={() => go(l.href)}
                  className="font-serif text-5xl tracking-tight text-stone-200 transition-colors duration-300 hover:text-amber-500"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.6, ease: EASE }}
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
